<template>
  <div class="bg-yellow-100 p-4 min-h-screen">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="mb-2 font-black text-black text-6xl text-center uppercase tracking-wider">
        🗂️ Inventory Manager
      </h1>
      <p class="font-bold text-gray-800 text-2xl text-center uppercase tracking-wide">
        Full Item Management
      </p>
      <div class="flex justify-center mt-4">
        <BrutalButton variant="secondary" @click="goBack">
          ← Back to Voice Interface
        </BrutalButton>
      </div>
    </div>

    <!-- Add New Item -->
    <div class="mx-auto mb-8 max-w-6xl">
      <BrutalCard title="Add New Item" color="green" shadow="lg">
        <div class="gap-4 grid grid-cols-1 md:grid-cols-5">
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Item Name</label>
            <input v-model="newItem.name" type="text" placeholder="Enter item name..."
              class="p-2 border-4 border-black rounded w-full font-medium" />
          </div>
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Quantity</label>
            <input v-model.number="newItem.current_quantity" type="number" min="0" step="1"
              class="p-2 border-4 border-black rounded w-full font-medium" />
          </div>
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Unit</label>
            <input v-model="newItem.unit" type="text" placeholder="pieces, lbs, etc."
              class="p-2 border-4 border-black rounded w-full font-medium" />
          </div>
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Category</label>
            <select v-model="newItem.category_id" class="p-2 border-4 border-black rounded w-full font-medium" required>
              <option value="" disabled>Select a category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          <div class="flex items-end">
            <BrutalButton variant="success" @click="addNewItem" :disabled="!isValidNewItem" class="w-full">
              Add Item
            </BrutalButton>
          </div>
        </div>
      </BrutalCard>
    </div>

    <!-- Add Edit Mode Toggle -->
    <div class="flex justify-end mx-auto mb-4 max-w-6xl">
      <BrutalButton :variant="editMode ? 'warning' : 'secondary'" @click="editMode = !editMode">
        {{ editMode ? 'Exit Edit Mode' : 'Enter Edit Mode' }}
      </BrutalButton>
    </div>
    <!-- Search and Filters -->
    <div class="mx-auto mb-8 max-w-6xl">
      <BrutalCard title="Search & Filters" color="white" shadow="lg">
        <div class="gap-4 grid grid-cols-1 md:grid-cols-3">
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Search Items</label>
            <input v-model="searchTerm" type="text" placeholder="Type item name..."
              class="p-2 border-4 border-black rounded w-full font-medium" />
          </div>
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Filter by Category</label>
            <select v-model="selectedCategory" class="p-2 border-4 border-black rounded w-full font-medium">
              <option value="">All Categories</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-2 font-bold text-sm uppercase tracking-wide">Filter by Status</label>
            <select v-model="selectedStatus" class="p-2 border-4 border-black rounded w-full font-medium">
              <option value="">All Items</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="in_stock">In Stock</option>
            </select>
          </div>
        </div>
      </BrutalCard>
    </div>

    <!-- Response Display -->
    <div v-if="responseMessage" class="mx-auto mb-8 max-w-6xl">
      <BrutalCard color="blue" shadow="md">
        <div class="text-center">
          <p class="font-medium text-lg">{{ responseMessage }}</p>
        </div>
      </BrutalCard>
    </div>

    <!-- Inventory Items -->
    <div class="mx-auto mb-8 max-w-6xl">
      <BrutalCard title="Inventory Items" color="white" shadow="lg">
        <div v-if="filteredItems.length === 0" class="py-8 text-center">
          <div class="mb-4 text-6xl">📦</div>
          <h3 class="mb-2 font-bold text-xl">No items found</h3>
          <p class="text-gray-600">Try adjusting your search or filters</p>
        </div>

        <div v-else class="space-y-4">
          <div v-for="item in filteredItems" :key="item.id"
            class="group hover:bg-gray-50 p-4 border-4 border-black rounded transition-colors">
            <div class="flex justify-between items-start">
              <!-- Item Info -->
              <div class="flex-1">
                <div class="flex items-center mb-2">
                  <div :class="[
                    'w-4 h-4 rounded-full mr-3',
                    item.stock_status === 'out_of_stock' ? 'bg-red-500' :
                      item.stock_status === 'low_stock' ? 'bg-yellow-500' : 'bg-green-500'
                  ]"></div>
                  <template v-if="editMode">
                    <input v-model="editNames[item.id]" @blur="updateName(item)" @keyup.enter="updateName(item)"
                      type="text"
                      class="bg-transparent focus:bg-white mr-2 px-1 border-b-2 border-black focus:outline-none font-bold text-xl"
                      :placeholder="item.name" style="max-width: 200px;" />
                  </template>
                  <template v-else>
                    <span class="mr-2 font-bold text-xl">{{ item.name }}</span>
                  </template>
                  <span v-if="item.category" class="ml-3 px-2 py-1 rounded font-bold text-xs uppercase tracking-wide"
                    :style="{ backgroundColor: item.category_color, color: '#000' }">
                    {{ item.category }}
                  </span>
                </div>

                <div class="gap-4 grid grid-cols-2 md:grid-cols-4 text-sm">
                  <div>
                    <span class="font-bold text-gray-600">Current:</span>
                    <template v-if="editMode">
                      <input v-model.number="editQuantities[item.id]" @blur="updateQuantity(item)"
                        @keyup.enter="updateQuantity(item)" type="number" min="0" step="1"
                        class="ml-1 px-2 py-1 border-2 border-black rounded w-16 font-bold text-center"
                        :placeholder="item.current_quantity" />
                    </template>
                    <template v-else>
                      <span class="ml-1">{{ item.formatted_quantity }}</span>
                    </template>
                  </div>
                  <div>
                    <span class="font-bold text-gray-600">Min Threshold:</span>
                    <template v-if="editMode">
                      <input v-model.number="editThresholds[item.id]" @blur="updateThreshold(item)"
                        @keyup.enter="updateThreshold(item)" type="number" min="1"
                        class="ml-1 px-2 py-1 border-2 border-black rounded w-20 font-bold text-center"
                        :placeholder="item.minimum_threshold" />
                      <span>{{ item.unit }}</span>
                    </template>
                    <template v-else>
                      <span class="ml-1">{{ item.minimum_threshold }} {{ item.unit }}</span>
                    </template>
                  </div>
                  <div>
                    <span class="font-bold text-gray-600">Category:</span>
                    <template v-if="editMode">
                      <select v-model="editCategories[item.id]" @change="updateCategory(item)"
                        class="ml-1 px-2 py-1 border-2 border-black rounded w-32 font-bold">
                        <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name
                        }}</option>
                      </select>
                    </template>
                    <template v-else>
                      <span class="ml-1">{{ item.category }}</span>
                    </template>
                  </div>
                  <div>
                    <span class="font-bold text-gray-600">Status:</span>
                    <span :class="[
                      'ml-1 px-2 py-1 rounded-full font-bold text-xs uppercase',
                      item.stock_status === 'out_of_stock' ? 'bg-red-200 text-red-800' :
                        item.stock_status === 'low_stock' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-green-200 text-green-800'
                    ]">
                      {{ item.stock_status.replace('_', ' ') }}
                    </span>
                  </div>
                  <div v-if="item.days_until_empty">
                    <span class="font-bold text-gray-600">Lasts:</span>
                    <span class="ml-1">{{ item.days_until_empty }} days</span>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex items-center space-x-2 ml-4">
                <!-- Quantity Controls -->
                <template v-if="editMode">
                  <div class="flex items-center space-x-1">
                    <button @click="quickAdjust(item, -1)" :disabled="isProcessing || item.current_quantity <= 0"
                      class="bg-red-400 hover:bg-red-500 disabled:opacity-50 border-2 border-black rounded w-8 h-8 font-bold text-white disabled:cursor-not-allowed"
                      title="Remove 1">
                      -1
                    </button>
                    <button @click="quickAdjust(item, 1)" :disabled="isProcessing"
                      class="bg-green-400 hover:bg-green-500 disabled:opacity-50 border-2 border-black rounded w-8 h-8 font-bold text-white disabled:cursor-not-allowed"
                      title="Add 1">
                      +1
                    </button>
                  </div>
                </template>
                <button @click="confirmDelete(item)" :disabled="isProcessing"
                  class="bg-red-600 hover:bg-red-700 disabled:opacity-50 border-2 border-black rounded w-8 h-8 font-bold text-white disabled:cursor-not-allowed"
                  title="Delete item completely">
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      </BrutalCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { router } from '@inertiajs/vue3'
import BrutalCard from '@/components/ui/BrutalCard.vue'
import BrutalButton from '@/components/ui/BrutalButton.vue'

// Props from the Rails controller
interface Props {
  grocery_items: any[]
  categories: any[]
}

const props = defineProps<Props>()

// Reactive state
const searchTerm = ref('')
const selectedCategory = ref('')
const selectedStatus = ref('')
const isProcessing = ref(false)
const responseMessage = ref('')
const editQuantities = ref<Record<number, number>>({})
const editThresholds = ref<Record<number, number>>({})
const editCategories = ref<Record<number, number | string>>({})
const editNames = ref<Record<number, string>>({})
const editMode = ref(false)

// New item form
const newItem = ref({
  name: '',
  current_quantity: 0,
  unit: 'pieces',
  minimum_threshold: 1,
  category_id: ''
})

// Computed filtered items
const filteredItems = computed(() => {
  let items = props.grocery_items

  // Filter by search term
  if (searchTerm.value) {
    items = items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }

  // Filter by category
  if (selectedCategory.value) {
    items = items.filter(item => item.category_id === parseInt(selectedCategory.value))
  }

  // Filter by status
  if (selectedStatus.value) {
    items = items.filter(item => item.stock_status === selectedStatus.value)
  }

  return items
})

// Add this computed property near other computed properties
const isValidNewItem = computed(() => {
  return newItem.value.name &&
    newItem.value.current_quantity >= 0 &&
    newItem.value.unit &&
    newItem.value.category_id
})

// Keyboard shortcut for edit mode (Alt+E)
const handleEditModeShortcut = (event: KeyboardEvent) => {
  if (event.altKey && (event.key === 'e' || event.key === 'E')) {
    event.preventDefault()
    editMode.value = !editMode.value
  }
}

// Initialize edit quantities
onMounted(() => {
  window.addEventListener('keydown', handleEditModeShortcut)
  props.grocery_items.forEach(item => {
    editQuantities.value[item.id] = item.current_quantity
    editThresholds.value[item.id] = item.minimum_threshold
    editCategories.value[item.id] = item.category_id
    editNames.value[item.id] = item.name
  })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEditModeShortcut)
})

// Functions
const goBack = () => {
  router.visit('/')
}

const quickAdjust = async (item: any, change: number) => {
  if (isProcessing.value) return
  isProcessing.value = true
  try {
    // Use the value from the input as the base
    let baseQuantity = editQuantities.value[item.id]
    if (baseQuantity === undefined || baseQuantity === null || String(baseQuantity) === "") {
      baseQuantity = item.current_quantity
    }
    let newQuantity = Math.max(0, Number(baseQuantity) + change)
    newQuantity = Math.round(newQuantity)
    if (!Number.isInteger(newQuantity)) {
      responseMessage.value = `Quantity must be an integer.`
      isProcessing.value = false
      return
    }
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: {
          name: item.name,
          current_quantity: parseInt(String(newQuantity), 10),
          minimum_threshold: parseInt(String(item.minimum_threshold), 10),
          unit: item.unit,
          category_id: parseInt(String(item.category_id), 10)
        }
      })
    })
    const result = await httpResponse.json()
    if (result.success) {
      responseMessage.value = `Updated ${item.name} to ${newQuantity} ${item.unit}`
      item.current_quantity = newQuantity
      editQuantities.value[item.id] = newQuantity
    } else {
      responseMessage.value = `Failed to update ${item.name}`
    }
  } catch (error) {
    responseMessage.value = `Error updating ${item.name}`
  } finally {
    isProcessing.value = false
  }
}

const updateQuantity = async (item: any) => {
  let newQuantity = editQuantities.value[item.id]
  if (newQuantity === undefined || newQuantity === null || String(newQuantity) === "") {
    responseMessage.value = `Quantity cannot be empty.`
    return
  }
  newQuantity = Math.round(newQuantity)
  if (!Number.isInteger(newQuantity)) {
    responseMessage.value = `Quantity must be an integer.`
    return
  }
  if (newQuantity === item.current_quantity || isProcessing.value) return
  isProcessing.value = true
  try {
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: {
          name: item.name,
          current_quantity: parseInt(String(newQuantity), 10),
          minimum_threshold: parseInt(String(item.minimum_threshold), 10),
          unit: item.unit,
          category_id: parseInt(String(item.category_id), 10)
        }
      })
    })
    const result = await httpResponse.json()
    if (result.success) {
      responseMessage.value = `Updated ${item.name} to ${newQuantity} ${item.unit}`
      item.current_quantity = newQuantity
      editQuantities.value[item.id] = newQuantity
    } else {
      responseMessage.value = `Failed to update ${item.name}`
    }
  } catch (error) {
    responseMessage.value = `Error updating ${item.name}`
  } finally {
    isProcessing.value = false
  }
}

const updateThreshold = async (item: any) => {
  const newThreshold = editThresholds.value[item.id]
  if (newThreshold === item.minimum_threshold || isProcessing.value) return
  isProcessing.value = true
  try {
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: {
          name: item.name,
          current_quantity: parseInt(String(item.current_quantity), 10),
          minimum_threshold: parseInt(String(newThreshold), 10),
          unit: item.unit,
          category_id: parseInt(String(item.category_id), 10)
        }
      })
    })
    let result
    try {
      result = await httpResponse.json()
    } catch (jsonErr) {
      responseMessage.value = `Error: Could not parse server response for threshold update.`
      isProcessing.value = false
      return
    }
    if (httpResponse.ok && result && result.success === true) {
      responseMessage.value = `Updated threshold for ${item.name} to ${newThreshold} ${item.unit}`
      item.minimum_threshold = newThreshold
    } else {
      responseMessage.value = `Failed to update threshold for ${item.name}`
    }
  } catch (error) {
    responseMessage.value = `Error updating threshold for ${item.name}`
  } finally {
    isProcessing.value = false
  }
}

const updateCategory = async (item: any) => {
  const newCategoryId = editCategories.value[item.id]
  if (newCategoryId === item.category_id || isProcessing.value) return
  isProcessing.value = true
  try {
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: {
          name: item.name,
          current_quantity: parseInt(String(item.current_quantity), 10),
          minimum_threshold: parseInt(String(item.minimum_threshold), 10),
          unit: item.unit,
          category_id: parseInt(String(newCategoryId), 10)
        }
      })
    })
    let result
    try {
      result = await httpResponse.json()
    } catch (jsonErr) {
      responseMessage.value = `Error: Could not parse server response for category update.`
      isProcessing.value = false
      return
    }
    if (httpResponse.ok && result && result.success === true) {
      responseMessage.value = `Updated category for ${item.name}`
      item.category_id = newCategoryId
      item.category = props.categories.find((cat: any) => cat.id === newCategoryId)?.name || item.category
      item.category_color = props.categories.find((cat: any) => cat.id === newCategoryId)?.color || item.category_color
    } else {
      responseMessage.value = `Failed to update category for ${item.name}`
    }
  } catch (error) {
    responseMessage.value = `Error updating category for ${item.name}`
  } finally {
    isProcessing.value = false
  }
}

const updateName = async (item: any) => {
  const newName = editNames.value[item.id]?.trim()
  if (!newName || newName === item.name || isProcessing.value) return
  isProcessing.value = true
  try {
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=\'csrf-token\']')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: {
          name: newName,
          current_quantity: parseInt(String(item.current_quantity), 10),
          minimum_threshold: parseInt(String(item.minimum_threshold), 10),
          unit: item.unit,
          category_id: parseInt(String(item.category_id), 10)
        }
      })
    })
    console.log('Rename response ok:', httpResponse.ok)
    let result
    try {
      result = await httpResponse.json()
    } catch (jsonErr) {
      responseMessage.value = `Error: Could not parse server response for rename.`
      isProcessing.value = false
      return
    }
    console.log('Rename result:', result)
    if (result.success) {
      responseMessage.value = `Updated name for item to ${newName}`
      item.name = newName
    } else {
      responseMessage.value = `Failed to update name for item.`
      editNames.value[item.id] = item.name
    }
  } catch (error) {
    responseMessage.value = `Error updating name for item.`
    editNames.value[item.id] = item.name
  } finally {
    isProcessing.value = false
  }
}

const confirmDelete = async (item: any) => {
  if (confirm(`Are you sure you want to completely delete "${item.name}" from your inventory? This cannot be undone.`)) {
    await deleteItem(item)
  }
}

const deleteItem = async (item: any) => {
  if (isProcessing.value) return

  isProcessing.value = true
  try {
    const httpResponse = await fetch(`/api/grocery_items/${item.id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      }
    })

    const result = await httpResponse.json()

    if (result.success) {
      responseMessage.value = `Successfully deleted ${item.name} from your inventory`
      // Remove from local array
      const index = props.grocery_items.findIndex(i => i.id === item.id)
      if (index !== -1) {
        props.grocery_items.splice(index, 1)
      }
    } else {
      responseMessage.value = `Failed to delete ${item.name}`
    }
  } catch (error) {
    responseMessage.value = `Error deleting ${item.name}`
  } finally {
    isProcessing.value = false
  }
}

const addNewItem = async () => {
  if (!newItem.value.name || isProcessing.value) return

  isProcessing.value = true
  try {
    const httpResponse = await fetch('/api/grocery_items', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({
        grocery_item: newItem.value
      })
    })

    const result = await httpResponse.json()

    if (result.success) {
      responseMessage.value = `Successfully added ${newItem.value.name} to your inventory`
      // Add to local array
      props.grocery_items.push(result.data)
      // Reset form
      newItem.value = {
        name: '',
        current_quantity: 0,
        unit: 'pieces',
        minimum_threshold: 1,
        category_id: ''
      }
    } else {
      responseMessage.value = `Failed to add ${newItem.value.name}`
    }
  } catch (error) {
    responseMessage.value = `Error adding ${newItem.value.name}`
  } finally {
    isProcessing.value = false
  }
}
</script>